
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { DimssaButton } from "@/components/shared/dimssa-button.vue";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";
import { normalizeHistoryDisplayDays } from "@/utils/cribHistory";
import * as Model from "@gigalot/data-models";
import lodash from "lodash";
import moment from "moment";
import Decimal from "decimal.js";
import type { CallIncrementType } from "@/data-manager";
import * as echarts from "echarts";
import type { ECharts } from "echarts";

type HistoryChartOption = echarts.EChartsCoreOption;
type CallTrendDirection = "up" | "down" | "same";
type HistorySection = "graph" | "table";
type StoredHistoryLayout = {
  sectionOrder?: unknown;
  showGraph?: unknown;
};
type ChartResizeObserver = {
  disconnect(): void;
  observe(target: Element): void;
};

const HISTORY_LAYOUT_STORAGE_KEY = "feeder-app.crib-score.history-layout";
const DEFAULT_HISTORY_SECTION_ORDER: HistorySection[] = ["graph", "table"];

@Component({
  components: {
    DimssaButton,
    BreadcrumbTrail,
  },
})
export default class Cribs extends Vue {
  @Prop() selectedKraal!: string;
  historyChart: ECharts | null = null;
  chartResizeObserver: ChartResizeObserver | null = null;
  chartResizeFrame: number | null = null;
  historySectionOrder: HistorySection[] = [...DEFAULT_HISTORY_SECTION_ORDER];
  showHistoryChart = true;
  draggedHistorySection: HistorySection | null = null;
  call: Decimal = new Decimal(-1);
  score = "";
  loading = false;
  swipeDirection = "None";
  edited = false;
  cribScores: Model.CribReadingItem[] = [];
  cribHistory: any = [];
  currentCribHistory: any = [];
  cribScoreHistoryDisplayDays = 0;
  page = 1;
  search = "";
  filter = {};
  sequence = 0;
  callIncrement: number = 1.0;
  callIncrementType: CallIncrementType = "kg/head";
  rations: any[] = [];
  blendPlans: any[] = [];
  feedingStrategyItems: any[] = [];
  useCribAliases = true;
  useAutoCribAdjustments = true;
  cribReadingAdjustments: any = {};
  newNote: string = "";

  keys = ["Kraal", "Ration", "Score", "Call", "Drive Order"];
  itemsPerPage = 1;
  cribHistoryHeader = [
    { text: "Date", value: "readingDate", align: "center" },
    { text: "Animals", value: "animalCount", align: "center" },
    { text: "Ration", value: "ration", align: "center" },
    { text: "Avg Call", value: "averageCall", align: "center" },
    { text: "Score", value: "score", align: "center" },
    { text: "Call", value: "call", align: "center" },
    { text: "Fed", value: "actualFed", align: "center" },
  ];

  getColor(status: string) {
    switch (status) {
      case "waiting":
        return "orange";
      case "skipped":
        return "red";
      case "blocked":
        return "grey";
      case "done":
        return "green";
    }
  }

  getGenderColor(gender: string) {
    if (gender === "Male") {
      return "light-blue";
    } else if (gender === "Female") return "pink";
    else return "grey";
  }

  getButtonState(score: number) {
    if (this.score === score.toString()) return "success";
    else return "ready";
  }

  created() {
    this.loadHistoryLayout();
  }

  mounted() {
    //TODO: Get dat from store instaed of calls (Should be in store from the cribSummary screen)
    //load kraal if kraal selected
    this.$store.dispatch("user/addFirebaseCallback", this.getCribPlansDb);
    window.addEventListener("resize", this.handleChartResize);

    //this.useCribAliases = JSON.parse(json.data.Configuration).useCribAliases;
    //this.useAutoCribAdjustments = JSON.parse(json.data.Configuration).useCribAutoAdjustments;
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.handleChartResize);
    if (this.chartResizeObserver) {
      this.chartResizeObserver.disconnect();
      this.chartResizeObserver = null;
    }
    if (this.chartResizeFrame !== null) {
      cancelAnimationFrame(this.chartResizeFrame);
      this.chartResizeFrame = null;
    }
    this.destroyHistoryChart();
  }

  @Watch("visibleCribHistory", { deep: true })
  onCurrentCribHistoryChanged() {
    this.renderHistoryChart();
  }

  @Watch("themeClass")
  onThemeChanged() {
    this.renderHistoryChart(true);
  }

  @Watch("showHistoryChart")
  onShowHistoryChartChanged(showHistoryChart: boolean) {
    this.saveHistoryLayout();
    if (showHistoryChart) {
      this.renderHistoryChart(true);
    } else {
      if (this.chartResizeObserver) {
        this.chartResizeObserver.disconnect();
      }
      this.destroyHistoryChart();
    }
  }

  get visibleHistorySections(): HistorySection[] {
    return this.historySectionOrder.filter((section) => section !== "graph" || this.showHistoryChart);
  }

  get visibleCribHistory(): any[] {
    const history = Array.isArray(this.currentCribHistory) ? this.currentCribHistory : [];
    const days = normalizeHistoryDisplayDays(this.cribScoreHistoryDisplayDays);
    if (!days || history.length === 0) return history;
    const dates = history.map((entry) => moment(entry.readingDate)).filter((date) => date.isValid());
    if (dates.length === 0) return [];
    const cutoff = moment.max(dates).clone().startOf("day").subtract(days - 1, "days");
    return history.filter((entry) => moment(entry.readingDate).isSameOrAfter(cutoff));
  }

  getHistorySectionLabel(section: HistorySection) {
    return section === "graph" ? "Score & Call history graph" : "History table";
  }

  toggleHistoryChart() {
    this.showHistoryChart = !this.showHistoryChart;
  }

  loadHistoryLayout() {
    try {
      const storedLayoutValue = localStorage.getItem(HISTORY_LAYOUT_STORAGE_KEY);
      if (!storedLayoutValue) return;

      const storedLayout = JSON.parse(storedLayoutValue) as StoredHistoryLayout;
      if (this.isValidHistorySectionOrder(storedLayout.sectionOrder)) {
        this.historySectionOrder = [...storedLayout.sectionOrder];
      }
      if (typeof storedLayout.showGraph === "boolean") {
        this.showHistoryChart = storedLayout.showGraph;
      }
    } catch (error) {
      console.warn("Could not load the saved crib score history layout", error);
    }
  }

  saveHistoryLayout() {
    try {
      localStorage.setItem(
        HISTORY_LAYOUT_STORAGE_KEY,
        JSON.stringify({
          sectionOrder: this.historySectionOrder,
          showGraph: this.showHistoryChart,
        })
      );
    } catch (error) {
      console.warn("Could not save the crib score history layout", error);
    }
  }

  isValidHistorySectionOrder(sectionOrder: unknown): sectionOrder is HistorySection[] {
    return (
      Array.isArray(sectionOrder) &&
      sectionOrder.length === DEFAULT_HISTORY_SECTION_ORDER.length &&
      DEFAULT_HISTORY_SECTION_ORDER.every((section) => sectionOrder.includes(section))
    );
  }

  startHistorySectionDrag(section: HistorySection, event: DragEvent) {
    this.draggedHistorySection = section;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", section);
    }
  }

  finishHistorySectionDrag() {
    this.draggedHistorySection = null;
  }

  dropHistorySection(targetSection: HistorySection, event: DragEvent) {
    event.preventDefault();
    const sourceSection = this.draggedHistorySection || (event.dataTransfer?.getData("text/plain") as HistorySection);
    this.draggedHistorySection = null;
    if (!DEFAULT_HISTORY_SECTION_ORDER.includes(sourceSection) || sourceSection === targetSection) return;

    const sourceIndex = this.historySectionOrder.indexOf(sourceSection);
    const targetIndex = this.historySectionOrder.indexOf(targetSection);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const sectionOrder = [...this.historySectionOrder];
    [sectionOrder[sourceIndex], sectionOrder[targetIndex]] = [sectionOrder[targetIndex], sectionOrder[sourceIndex]];
    this.updateHistorySectionOrder(sectionOrder);
  }

  moveHistorySection(section: HistorySection, direction: -1 | 1) {
    const sourceIndex = this.historySectionOrder.indexOf(section);
    const targetIndex = sourceIndex + direction;
    if (sourceIndex < 0 || targetIndex < 0 || targetIndex >= this.historySectionOrder.length) return;

    const sectionOrder = [...this.historySectionOrder];
    [sectionOrder[sourceIndex], sectionOrder[targetIndex]] = [sectionOrder[targetIndex], sectionOrder[sourceIndex]];
    this.updateHistorySectionOrder(sectionOrder);
  }

  swapHistorySections() {
    this.updateHistorySectionOrder([...this.historySectionOrder].reverse());
  }

  updateHistorySectionOrder(sectionOrder: HistorySection[]) {
    this.historySectionOrder = sectionOrder;
    this.saveHistoryLayout();
    if (this.showHistoryChart) {
      this.renderHistoryChart();
    }
  }

  formatDecimal(number: string) {
    return parseFloat(number).toFixed(2);
  }

  getCallTrendIcon(item: any) {
    if (item?.callTrendDirection === "up") return "mdi-arrow-up-bold";
    if (item?.callTrendDirection === "down") return "mdi-arrow-down-bold";
    return "mdi-minus";
  }

  getCallTrendColor(item: any) {
    if (item?.callTrendDirection === "up") return "green darken-1";
    if (item?.callTrendDirection === "down") return "red darken-1";
    return "grey";
  }

  getCallTrendTitle(item: any) {
    const dayLabel = item?.callTrendStreak === 1 ? "day" : "days";
    if (item?.callTrendDirection === "up") return `Call increased ${item.callTrendStreak} ${dayLabel} in a row`;
    if (item?.callTrendDirection === "down") return `Call decreased ${item.callTrendStreak} ${dayLabel} in a row`;
    if (!item?.callTrendHasPrevious) return "No previous call to compare";
    return "Call stayed the same";
  }

  get callChangePercentage(): number | null {
    const history = Array.isArray(this.currentCribHistory) ? this.currentCribHistory : [];
    if (history.length === 0) return null;

    try {
      const previousCall = new Decimal(history[history.length - 1]?.call);
      const currentCall = new Decimal(this.boundcall as any);
      if (!previousCall.isFinite() || !currentCall.isFinite() || previousCall.isZero()) return null;
      return Number(currentCall.minus(previousCall).div(previousCall.abs()).mul(100).toFixed(1));
    } catch (error) {
      return null;
    }
  }

  get formattedCallChangePercentage(): string {
    const percentage = this.callChangePercentage;
    if (percentage === null) return "--";
    if (percentage > 0) return `+${percentage.toFixed(1)}%`;
    return `${percentage.toFixed(1)}%`;
  }

  get callChangeClass(): string {
    const percentage = this.callChangePercentage;
    if (percentage === null || percentage === 0) return "grey--text";
    return percentage > 0 ? "green--text text--darken-1" : "red--text text--darken-1";
  }

  get callChangeTitle(): string {
    const history = Array.isArray(this.currentCribHistory) ? this.currentCribHistory : [];
    if (history.length === 0) return "No previous call available";
    const previousCall = this.toChartNumber(history[history.length - 1]?.call);
    if (previousCall === null || previousCall === 0) return "Percentage unavailable because the previous call is zero";
    return `Change from previous call (${previousCall.toFixed(2)})`;
  }

  handleChartResize = () => {
    if (this.chartResizeFrame !== null) {
      cancelAnimationFrame(this.chartResizeFrame);
    }
    this.chartResizeFrame = requestAnimationFrame(() => {
      this.chartResizeFrame = null;
      this.renderHistoryChart();
    });
  };

  destroyHistoryChart() {
    if (this.historyChart) {
      this.historyChart.dispose();
      this.historyChart = null;
    }
  }

  getChartContainer(): HTMLDivElement | null {
    const chartRef = this.$refs.historyChart;
    if (Array.isArray(chartRef)) {
      return (chartRef[0] as HTMLDivElement) ?? null;
    }
    return (chartRef as HTMLDivElement) ?? null;
  }

  observeChartContainer(chartContainer: HTMLDivElement) {
    const ResizeObserverCtor = (window as any).ResizeObserver as (new (callback: () => void) => ChartResizeObserver) | undefined;
    if (!ResizeObserverCtor) return;
    if (!this.chartResizeObserver) {
      this.chartResizeObserver = new ResizeObserverCtor(() => {
        this.handleChartResize();
      });
    }

    this.chartResizeObserver.disconnect();
    this.chartResizeObserver.observe(chartContainer);
  }

  getHistoryChartOptions(): HistoryChartOption {
    const history = this.visibleCribHistory;
    const chartWidth = this.getChartContainer()?.clientWidth ?? window.innerWidth ?? 1024;
    const isCompact = chartWidth < 600;
    const isVeryCompact = chartWidth < 420;
    const axisLabels = history.map((entry) => moment(entry.readingDate).format("D MMM"));
    const scoreData = history.map((entry) => {
      const score = this.getScore(entry);
      return typeof score === "number" && Number.isFinite(score) ? score : null;
    });
    const callData = history.map((entry) => this.toChartNumber(entry.call));
    const fedData = history.map((entry) => this.toChartNumber(entry.actualFed));
    const isDark = this.themeClass?.toLowerCase().includes("dark");
    const axisColor = isDark ? "#d1d5db" : "#4b5563";
    const gridColor = isDark ? "rgba(148, 163, 184, 0.18)" : "rgba(71, 85, 105, 0.16)";
    const tooltipBackground = isDark ? "#111827" : "#ffffff";
    const tooltipText = isDark ? "#f9fafb" : "#111827";
    const axisFontSize = isCompact ? 11 : 12;

    return {
      animationDuration: 350,
      color: ["#64b5f6", "#2e7d32", "#1565c0"],
      grid: {
        top: isCompact ? 64 : 72,
        left: isVeryCompact ? 8 : isCompact ? 18 : 50,
        right: isVeryCompact ? 8 : isCompact ? 18 : 50,
        bottom: isVeryCompact ? 52 : isCompact ? 44 : 32,
        containLabel: true,
      },
      legend: {
        type: isCompact ? "scroll" : "plain",
        top: 8,
        left: isCompact ? 0 : 8,
        right: isCompact ? 0 : undefined,
        itemWidth: isCompact ? 12 : 18,
        itemHeight: isCompact ? 8 : 12,
        textStyle: {
          color: axisColor,
          fontSize: axisFontSize,
        },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: tooltipBackground,
        borderColor: gridColor,
        textStyle: {
          color: tooltipText,
        },
      },
      xAxis: {
        type: "category",
        data: axisLabels,
        boundaryGap: true,
        axisLabel: {
          color: axisColor,
          fontSize: axisFontSize,
          rotate: isVeryCompact ? 28 : 0,
          hideOverlap: true,
        },
        axisLine: {
          lineStyle: {
            color: gridColor,
          },
        },
      },
      yAxis: [
        {
          type: "value",
          name: isCompact ? "kg" : "Call / Fed (kg)",
          nameLocation: isCompact ? "end" : "middle",
          nameRotate: isCompact ? 0 : 90,
          nameGap: isCompact ? 18 : 36,
          axisLabel: {
            color: axisColor,
            fontSize: axisFontSize,
          },
          nameTextStyle: {
            color: axisColor,
            fontSize: axisFontSize,
          },
          splitLine: {
            lineStyle: {
              color: gridColor,
            },
          },
        },
        {
          type: "value",
          name: isCompact ? "" : "Score",
          nameLocation: isCompact ? "end" : "middle",
          nameRotate: isCompact ? 0 : -90,
          nameGap: isCompact ? 0 : 30,
          axisLabel: {
            color: axisColor,
            fontSize: axisFontSize,
          },
          nameTextStyle: {
            color: axisColor,
            fontSize: axisFontSize,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: "Call",
          type: "line",
          yAxisIndex: 0,
          smooth: true,
          symbol: "circle",
          symbolSize: isCompact ? 6 : 8,
          lineStyle: {
            width: isCompact ? 2 : 3,
            color: "#2e7d32",
          },
          itemStyle: {
            color: "#2e7d32",
          },
          data: callData,
        },
        {
          name: "Fed",
          type: "line",
          yAxisIndex: 0,
          smooth: true,
          symbol: "circle",
          symbolSize: isCompact ? 6 : 8,
          lineStyle: {
            width: isCompact ? 2 : 3,
            color: "#1565c0",
          },
          itemStyle: {
            color: "#1565c0",
          },
          data: fedData,
        },
        {
          name: "Score",
          type: "bar",
          yAxisIndex: 1,
          barMaxWidth: isCompact ? 22 : 34,
          itemStyle: {
            color: "#64b5f6",
            borderRadius: [4, 4, 0, 0],
          },
          data: scoreData,
        },
      ],
    };
  }

  toChartNumber(value: unknown): number | null {
    if (value === null || value === undefined) return null;
    const numberValue = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numberValue) ? parseFloat(numberValue.toFixed(2)) : null;
  }

  async renderHistoryChart(forceRecreate = false) {
    await this.$nextTick();
    const chartContainer = this.getChartContainer();
    if (!chartContainer) return;
    this.observeChartContainer(chartContainer);

    if (forceRecreate) {
      this.destroyHistoryChart();
    }

    if (!this.historyChart) {
      this.historyChart = echarts.init(chartContainer, undefined, { renderer: "canvas" });
    }

    this.historyChart.setOption(this.getHistoryChartOptions(), true);
    this.historyChart.resize();
  }

  getCallIncrementAmount(previousCall: string | number): Decimal {
    const increment = new Decimal(this.callIncrement);
    if (this.callIncrementType === "percentage") {
      return new Decimal(previousCall).mul(increment).div(100);
    }

    if (!this.cribReadingAdjustments[0].callMoisture) {
      return increment.div(this.getDryMatterFactor(this.rationGuid));
    }

    return increment;
  }

  adjustCall(seq: number, previousCall: string | number, direction: -1 | 1) {
    const previous = new Decimal(previousCall);
    const adjustment = this.getCallIncrementAmount(previousCall).mul(direction);
    this.call = previous.plus(adjustment);
    this.cribScores[this.getIndex(seq)].call = parseFloat(this.call.toFixed(6));
    this.edited = true;
  }

  decrement(seq: number, previousCall: string) {
    this.adjustCall(seq, previousCall, -1);
  }

  increment(seq: number, previousCall: string) {
    this.adjustCall(seq, previousCall, 1);
  }

  getScore(item: Model.CribReadingItem) {
    if (item.score === -1) {
      return item.status;
    } else return item.score;
  }

  getAlias(scored: number) {
    if (this.cribReadingAdjustments[0].useAliases) {
      if (this.cribReadingAdjustments.length > 0) {
        let alias: any = lodash.find(this.cribReadingAdjustments[0].cribReadingAdjustment, (yes) => {
          if (yes.score == scored) return yes;
        });
        if (!alias) {
          return "-";
        }
        return alias?.userAlias;
      }
    } else return scored;
  }

  adjustedScore(score: number): number {
    let adjustment = lodash.find(this.cribReadingAdjustments[0].cribReadingAdjustment, { score: score });
    // return 2;
    return adjustment?.autoCallAdjustment ?? (this.usePrecentageAdjustment ? 100 : 0);
  }

  get usePrecentageAdjustment(): boolean {
    return !!this.cribReadingAdjustments?.[0]?.usePrecentageAdjustment;
  }

  getAutoAdjustedCall(previousCall: string | number, adjustment: number): Decimal {
    const call = new Decimal(Number(previousCall) || 0);
    const autoAdjustment = new Decimal(Number(adjustment) || 0);
    if (this.usePrecentageAdjustment) {
      return call.mul(autoAdjustment).div(100);
    }

    return call.plus(autoAdjustment);
  }

  doAutoAdjustment(score: number, previousCall: string, item: any) {
    if (item.score === -1 && !this.cribReadingAdjustments[0].callMoisture) {
      let dmf = this.getDryMatterFactor(this.rationGuid);
      let c = new Decimal(previousCall);
      previousCall = c.div(dmf).toFixed(2);
    }

    this.select(score, item.sequence);
    item.score = score;
    if (this.useAutoCribAdjustments) {
      const autoCallAdjustment = this.adjustedScore(score);
      if (!this.cribReadingAdjustments[0].callMoisture) {
        if (this.usePrecentageAdjustment) {
          item.call = parseFloat(this.getAutoAdjustedCall(previousCall, autoCallAdjustment).toFixed(6));
        } else {
          let dmf = this.getDryMatterFactor(this.rationGuid);
          let c = new Decimal(autoCallAdjustment);
          let d = parseFloat(c.div(dmf).toFixed(6));
          item.call = parseFloat(this.getAutoAdjustedCall(previousCall, d).toFixed(6));
        }
      } else {
        item.call = parseFloat(this.getAutoAdjustedCall(previousCall, autoCallAdjustment).toFixed(6));
      }
    }
  }

  doCalculatedAdjustment(score: number, cribReading: any) {
    //subtract leftover from yesterdays fed amount
    this.select(score, cribReading.sequence);
    if (score < 0) score = 0;
    cribReading.score = score;

    if (cribReading.animalCount) {
      let previousActualFed = this.currentCribHistory[this.currentCribHistory.length - 1].actualFed;
      let yesterdayCribScore = this.currentCribHistory[this.currentCribHistory.length - 1].score;
      if (yesterdayCribScore < 0) yesterdayCribScore = 0; //for safety (if not scored to not go negative)
      let totalFeedInCrib = previousActualFed * cribReading.animalCount + yesterdayCribScore;
      if (totalFeedInCrib < 0) totalFeedInCrib = 0; //for safety (cant have negative feed in crib)
      let feedConsumed = totalFeedInCrib - (score ?? 0);
      let challengeAmount = this.getChallengePercentage(this.rationGuid);
      let challenge = (1 + challengeAmount / 100) * feedConsumed;
      let allocatedFeed = (challenge - score) / cribReading.animalCount;

      if (allocatedFeed < 0) allocatedFeed = 0;
      if (!this.cribReadingAdjustments[0].callMoisture) {
        let dmf = this.getDryMatterFactor(this.rationGuid);
        let c = new Decimal(parseFloat(allocatedFeed.toFixed(6)));
        let d = parseFloat(c.div(dmf).toFixed(6));
        cribReading.call = parseFloat(d.toFixed(6));
      } else {
        cribReading.call = parseFloat(allocatedFeed.toFixed(6));
      }
    }
  }

  dOldCalculatedAdjustment(leftover: number, previousCall: string, item: any) {
    //subtract leftover from yesterdays fed amount
    this.select(leftover, item.sequence);
    item.score = leftover;

    let yesterdaysAmount = 0;
    if (item.animalCount) {
      yesterdaysAmount = parseFloat(previousCall) - leftover / item.animalCount;
    } else {
      yesterdaysAmount = parseFloat(previousCall);
    }
    //add challenge amount
    let challengeAmount = this.getChallengePercentage(this.rationGuid);

    let newAmount = yesterdaysAmount;

    if (leftover > 5) {
      newAmount = (1 + challengeAmount / 100) * yesterdaysAmount;
    }
    if (item.animalCount) {
      newAmount = newAmount - leftover / item.animalCount;
    } else {
      newAmount = newAmount;
    }
    if (newAmount < 0) newAmount = 0;

    if (!this.cribReadingAdjustments[0].callMoisture) {
      let dmf = this.getDryMatterFactor(this.rationGuid);
      let c = new Decimal(parseFloat(newAmount.toFixed(6)));
      let d = parseFloat(c.div(dmf).toFixed(6));
      item.call = parseFloat(d.toFixed(6));
    } else {
      item.call = parseFloat(newAmount.toFixed(6));
    }
  }

  gotoKraal(event: any) {
    this.$router.push(`/cribscores?selectedKraal=${event.kraalId}`);
    this.$router.go(0);
  }

  getDryMatterFactor(rationGuid: string) {
    const blendDmf = this.getBlendDryMatterFactor(rationGuid);
    if (blendDmf !== null) return blendDmf;
    return this.getRationDryMatterFactor(rationGuid);
  }

  getRationDryMatterFactor(rationGuid: string) {
    for (let i = 0; i < this.rations.length; i++) {
      if (this.rations[i].guid === rationGuid) {
        return this.rations[i].dryMatterFactor?.dryMatterFactor ? this.rations[i].dryMatterFactor.dryMatterFactor : 1.0;
      }
    }
    return 1.0;
  }

  getBlendDryMatterFactor(rationGuid: string): number | null {
    const blendPlan = this.getBlendPlanForRation(rationGuid);
    if (!blendPlan) return null;

    const dayPlan = this.getBlendDayPlan(blendPlan, rationGuid);
    const sessions = dayPlan?.sessions ?? [];
    if (!Array.isArray(sessions) || sessions.length === 0) return null;

    const strategyItems = Array.isArray(this.feedingStrategyItems) ? this.feedingStrategyItems : [];
    const strategyBySession = new Map<number, number>();
    for (const item of strategyItems) {
      const sessionNumber = Number(item?.sequence);
      const percentage = Number(item?.percentage);
      if (!Number.isNaN(sessionNumber) && !Number.isNaN(percentage)) {
        strategyBySession.set(sessionNumber, percentage);
      }
    }

    let weightedSum = new Decimal(0);
    let totalWeight = new Decimal(0);
    let sum = new Decimal(0);
    let count = 0;

    for (const session of sessions) {
      const sessionDmf = this.getSessionBlendDryMatterFactor(session);
      if (sessionDmf === null) continue;
      sum = sum.plus(sessionDmf);
      count += 1;
      const weight = strategyBySession.get(Number(session?.session)) ?? 0;
      if (weight > 0) {
        weightedSum = weightedSum.plus(new Decimal(sessionDmf).mul(weight));
        totalWeight = totalWeight.plus(weight);
      }
    }

    if (totalWeight.gt(0)) return parseFloat(weightedSum.div(totalWeight).toFixed(6));
    if (count > 0) return parseFloat(sum.div(count).toFixed(6));
    return null;
  }

  getSessionBlendDryMatterFactor(session: any): number | null {
    const rationAPct = Number(session?.rationAPct ?? 0);
    const rationBPct = Number(session?.rationBPct ?? 0);
    const totalPct = rationAPct + rationBPct;
    if (totalPct <= 0) return null;
    const dmfA = session?.rationAGuid ? this.getRationDryMatterFactor(session.rationAGuid) : 1.0;
    const dmfB = session?.rationBGuid ? this.getRationDryMatterFactor(session.rationBGuid) : 1.0;
    return (dmfA * rationAPct + dmfB * rationBPct) / totalPct;
  }

  getBlendPlanForRation(rationGuid: string) {
    if (!Array.isArray(this.blendPlans) || this.blendPlans.length === 0) return null;
    let blendPlan = this.blendPlans.find((bp) => bp.guid === rationGuid);
    if (blendPlan) return blendPlan;

    const ration = this.rations.find((r) => r.guid === rationGuid);
    if (ration?.blendPlanGuid) {
      blendPlan = this.blendPlans.find((bp) => bp.guid === ration.blendPlanGuid);
      if (blendPlan) return blendPlan;
    }

    if (ration?.type === "blend") {
      blendPlan = this.blendPlans.find((bp) => bp.guid === rationGuid);
      if (blendPlan) return blendPlan;
    }

    return null;
  }

  getBlendDayPlan(blendPlan: any, rationGuid: string) {
    const dayPlans = Array.isArray(blendPlan?.dayPlans) ? blendPlan.dayPlans : [];
    if (dayPlans.length === 0) return null;
    const dayCount = Math.max(dayPlans.length, Number(blendPlan?.days ?? 0) || 0);
    if (dayCount <= 0) return dayPlans[0];
    const daysOnRation = this.getDaysOnRation(rationGuid);
    const dayIndex = (daysOnRation - 1) % dayCount;
    return dayPlans.find((dp: any) => dp.day === dayIndex + 1) || dayPlans[dayIndex] || dayPlans[0];
  }

  getDaysOnRation(rationGuid: string) {
    let days = 1;
    const index = this.getIndex(this.sequence);
    const currentItem = index >= 0 ? this.cribScores[index] : undefined;
    const rationName = currentItem?.ration;
    const history = Array.isArray(this.currentCribHistory) ? this.currentCribHistory : [];
    if (history.length > 0) {
      const latest = history[history.length - 1];
      const latestMatches = latest?.rationGuid === rationGuid || (rationName && latest?.ration === rationName);
      if (latestMatches) {
        for (let i = history.length - 1; i >= 0; i--) {
          const entry = history[i];
          const matchesGuid = entry?.rationGuid === rationGuid;
          const matchesName = rationName && entry?.ration === rationName;
          if (matchesGuid || matchesName) {
            days += 1;
          } else {
            break;
          }
        }
      }
    }
    return days;
  }

  getChallengePercentage(rationGuid: string) {
    for (let i = 0; i < this.rations.length; i++) {
      if (this.rations[i].guid === rationGuid) {
        return this.rations[i].challengePercentage ? this.rations[i].challengePercentage : 0;
      }
    }
    return 0;
  }

  getCallPerHead(rationGuid: string, wet: boolean) {
    wet = this.cribReadingAdjustments[0].callMoisture === wet;

    if (this.cribReadingAdjustments[0].callMoisture) {
      if (wet) {
        let a = new Decimal(this.boundcall);
        return a.toFixed(3);
      } else {
        let a = new Decimal(this.boundcall);
        let dmf = this.getDryMatterFactor(rationGuid);
        let d = a.mul(dmf);
        return d.toFixed(3);
      }
    } else {
      if (wet) {
        let a = new Decimal(this.boundcall);
        return a.toFixed(3);
      } else {
        let a = new Decimal(this.boundcall);
        let dmf = this.getDryMatterFactor(rationGuid);
        let d = a.div(dmf);
        return d.toFixed(3);
      }
    }
  }

  getTotalCall(rationGuid: string, wet: boolean) {
    if (this.boundAnimals) {
      wet = this.cribReadingAdjustments[0].callMoisture === wet;

      if (this.cribReadingAdjustments[0].callMoisture) {
        if (wet) {
          let a = new Decimal(this.boundcall);
          let b = new Decimal(this.boundAnimals);
          let c = a.mul(b);
          return c.toFixed(0);
        } else {
          let a = new Decimal(this.boundcall);
          let b = new Decimal(this.boundAnimals);
          let c = a.mul(b);
          let dmf = this.getDryMatterFactor(rationGuid);
          let d = c.mul(dmf);
          return d.toFixed(0);
        }
      } else {
        if (wet) {
          let a = new Decimal(this.boundcall);
          let b = new Decimal(this.boundAnimals);
          let c = a.mul(b);
          return c.toFixed(0);
        } else {
          let a = new Decimal(this.boundcall);
          let b = new Decimal(this.boundAnimals);
          let c = a.mul(b);
          let dmf = this.getDryMatterFactor(rationGuid);
          let d = c.div(dmf);
          return d.toFixed(0);
        }
      }
    }
  }

  get boundscore() {
    let index = this.getIndex(this.sequence);
    if (this.cribScores[index].score === -1) return "#";
    else return this.cribScores[index].score;
  }

  get boundcall() {
    //(wet)

    let index = this.getIndex(this.sequence);
    if (this.cribScores[index].score === -1) {
      if (this.currentCribHistory.length > 0) {
        if (this.cribReadingAdjustments[0].callonPreviousCall) {
          this.cribScores[index].call = this.currentCribHistory[this.currentCribHistory.length - 1].call.toFixed(3);
          return this.currentCribHistory[this.currentCribHistory.length - 1].call.toFixed(3);
        } else {
          this.cribScores[index].call = this.currentCribHistory[this.currentCribHistory.length - 1].actualFed.toFixed(3);
          return this.currentCribHistory[this.currentCribHistory.length - 1].actualFed.toFixed(3);
        }
      } else {
        if (!this.cribReadingAdjustments[0].callMoisture) {
          let dmf = this.getDryMatterFactor(this.rationGuid);
          let c = new Decimal(this.cribScores[index].call ?? 0);
          let d = c.mul(dmf);
          return d;
        } else return this.cribScores[index].call;
      }
    } else {
      if (!this.cribReadingAdjustments[0].callMoisture) {
        let dmf = this.getDryMatterFactor(this.rationGuid);
        let c = new Decimal(this.cribScores[index].call ?? 0);
        let d = c.mul(dmf);
        return d;
      } else return this.cribScores[index].call;
    }
  }

  get rationGuid() {
    let index = this.getIndex(this.sequence);
    return this.cribScores[index].rationGuid;
  }

  get boundAnimals() {
    let index = this.getIndex(this.sequence);
    return this.cribScores[index].animalCount ? this.cribScores[index].animalCount : 0;
  }

  set boundcall(value: any) {
    let index = this.getIndex(this.sequence);
    this.call = value;
    this.cribScores[index].call = value;
    this.edited = true;
  }

  async getCribPlansDb() {
    this.loading = true;
    try {
      console.log("getCribPlansDb()");
      this.cribScores = await this.$store.dispatch("data/getTodaysCribScores");
      this.cribReadingAdjustments = await this.$store.dispatch("data/getCribReadingAdjustments");
      this.rations = await this.$store.dispatch("data/getRationDMFs");
      this.blendPlans = await this.$store.dispatch("data/getBlendPlans");
      const strategyItems = await this.$store.dispatch("data/getFeedingStrategy");
      const strategy = Array.isArray(strategyItems) ? strategyItems[0] : strategyItems;
      this.feedingStrategyItems = lodash.sortBy(strategy?.FeedingPlanItems ?? [], "sequence");
      this.callIncrement = parseFloat(await this.$store.dispatch("data/getCallIncrement"));
      this.callIncrementType = await this.$store.dispatch("data/getCallIncrementType");
      this.cribScoreHistoryDisplayDays = normalizeHistoryDisplayDays(await this.$store.dispatch("data/getCribScoreHistoryDisplayDays"));
      this.cribScores = lodash.sortBy(this.cribScores, function (o) {
        return o.kraalId;
      });
      console.log("Done Downloading");
      this.cribHistory = await this.$store.dispatch("data/getCribScoreHistory");
    } catch (err) {
      console.log("getCribPlans -> error: " + err);
    } finally {
      if (this.selectedKraal) {
        console.log(this.selectedKraal);
        let index = 0;
        let sequence = this.getSequence(this.selectedKraal);
        if (sequence > -1) {
          this.page = sequence + 1;
          this.sequence = sequence;
          let index2 = this.getIndex(sequence);
          this.currentCribHistory = this.getcribHistory(this.cribScores[index2].kraalId);
        }

        if (this.currentCribHistory.length > 0) {
          this.call = new Decimal(this.currentCribHistory[this.currentCribHistory.length - 1].call);
        }
        console.log(`setting page index to ${this.page}`);
      } else console.log("nothing in selected Kraal");

      this.loading = false;
      this.renderHistoryChart();
      //default data
    }
  }

  getIndex(sequence: number) {
    let index = lodash.findIndex(this.cribScores, { sequence: sequence });
    return index;
  }
  getpageIndex(kraalId: string) {
    let index = lodash.findIndex(this.cribScores, { kraalId: kraalId }) + 1;

    return index;
  }
  getSequence(kraalId: string) {
    let seq = -1;
    let item = lodash.find(this.cribScores, { kraalId: kraalId });
    if (item) {
      if (item.sequence != undefined) {
        return item.sequence;
      }
    } else return seq;
    return seq;
  }

  getcribHistory(kraalId: string) {
    let history: any[] = [];
    for (let day of this.cribHistory) {
      try {
        let hs = lodash.clone(lodash.find(day.cribScores, { kraalId: kraalId }));
        if (hs) {
          if (!this.cribReadingAdjustments[0].callMoisture) {
            let dmf = this.getDryMatterFactor(this.rationGuid);
            let c = new Decimal(hs.call);
            let d = new Decimal(hs.actualFed);
            hs.call = new Decimal(c.mul(dmf).toFixed(3));
            hs.actualFed = d.mul(dmf);
          }
          history.push(hs);
        }
      } catch (err) {
        console.warn(`Kraal ${kraalId} had no feed amount for day`);
      }
    }
    const orderedHistory = lodash.orderBy(history, ["readingDate"], ["asc"]);
    let previousTrendDirection: CallTrendDirection = "same";
    let previousTrendStreak = 0;
    orderedHistory.forEach((entry, index, arr) => {
      const windowStart = Math.max(0, index - 4);
      const windowEntries = arr.slice(windowStart, index + 1);
      if (windowEntries.length < 5 || windowEntries.some((historyItem) => historyItem.call === null || historyItem.call === undefined)) {
        entry.averageCall = null;
      } else {
        const sum = windowEntries.reduce((acc, historyItem) => acc.plus(historyItem.call), new Decimal(0));
        entry.averageCall = sum.div(windowEntries.length);
      }

      const currentCall = this.toRoundedCallDecimal(entry.call);
      const previousCall = index > 0 ? this.toRoundedCallDecimal(arr[index - 1].call) : null;
      let trendDirection: CallTrendDirection = "same";
      let trendStreak = 0;

      if (currentCall && previousCall) {
        if (currentCall.gt(previousCall)) {
          trendDirection = "up";
        } else if (currentCall.lt(previousCall)) {
          trendDirection = "down";
        }
      }

      if (trendDirection !== "same") {
        trendStreak = trendDirection === previousTrendDirection ? previousTrendStreak + 1 : 1;
      }

      entry.callTrendDirection = trendDirection;
      entry.callTrendStreak = trendStreak;
      entry.callTrendHasPrevious = !!(currentCall && previousCall);
      previousTrendDirection = trendDirection;
      previousTrendStreak = trendStreak;
    });
    return orderedHistory;
  }

  toRoundedCallDecimal(value: unknown): Decimal | null {
    if (value === null || value === undefined) return null;
    try {
      const decimal = new Decimal(value as any);
      if (!decimal.isFinite()) return null;
      return new Decimal(decimal.toFixed(2));
    } catch (err) {
      return null;
    }
  }
  get themeClass(): string {
    return this.$store.state.lightDarkMode;
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Crib Scores", disabled: true });
  }

  async updateServer(editedItem?: any) {
    console.log(editedItem);
    if (editedItem) {
      editedItem.actualFed = 0; //??
      editedItem.call = parseFloat(this.getCallPerHead(editedItem.rationGuid, true));
      try {
        console.log("updateServer()");
        let json = await this.$store.dispatch("cribScores/save", editedItem);
      } catch (err) {
        console.log(err);
        throw err;
      }
    }
  }
  saveNote(cribscore: Model.CribReadingItem) {
    let note = new Model.NoteItem();
    note.text = this.newNote;
    note.timestamp = Date.now();
    if (!cribscore.notes) {
      cribscore.notes = [];
    }
    cribscore.notes?.push(note);
    this.updateServer(cribscore);
  }
  get moment() {
    return this.$store.state.moment;
  }

  get numberOfPages() {
    return Math.ceil(this.cribScores.length / this.itemsPerPage);
  }

  nextPage() {
    let index = this.getIndex(this.sequence);
    //save crib score before going to next page (if something changed)
    if (this.edited) this.updateServer(this.cribScores[index]);

    if (this.sequence <= this.numberOfPages) this.sequence += 1;
    if (this.page + 1 <= this.numberOfPages) this.page += 1;
    //goto summary page
    else this.$router.push(`/outbox`);

    index = this.getIndex(this.sequence);
    this.currentCribHistory = this.getcribHistory(this.cribScores[index].kraalId);
    this.call = new Decimal(this.boundcall);
    this.score = "";
    this.edited = false;
    this.renderHistoryChart(true);
  }

  formerPage() {
    let index = this.getIndex(this.sequence);
    if (this.edited) this.updateServer(this.cribScores[index]);

    if (this.page > 1) this.page -= 1;
    if (this.sequence > 0) this.sequence -= 1;
    index = this.getIndex(this.sequence);
    this.currentCribHistory = this.getcribHistory(this.cribScores[index].kraalId);
    this.call = new Decimal(this.boundcall);
    this.score = "";
    this.edited = false;
    this.renderHistoryChart(true);
  }

  updateItemsPerPage(number: number) {
    this.itemsPerPage = number;
  }

  back(seq: number) {
    this.sequence = seq;
    this.formerPage();
  }

  block(seq: number) {
    //Set kraal status to blocked
    let index = this.getIndex(seq);
    this.cribScores[index].status = "blocked";
    this.edited = true;
    this.sequence = seq;
    this.nextPage();
  }

  unblock(seq: number) {
    //Set kraal status to blocked
    let index = this.getIndex(seq);
    this.cribScores[index].status = "done";
    this.edited = true;
    this.sequence = seq;
    this.nextPage();
  }

  skip(seq: number) {
    //Set kraal status to skipped
    let index = this.getIndex(seq);
    this.cribScores[index].status = "skipped";
    this.edited = true;
    this.sequence = seq;
    this.nextPage();
  }

  select(score: number, seq: number) {
    this.score = score.toString();
    let index = this.getIndex(seq);
    this.cribScores[index].score = score;
    this.cribScores[index].status = "done";
    this.edited = true;
  }
}
